import { Injectable } from '@angular/core';
import { ActionPerformed, PushNotifications, Token } from '@capacitor/push-notifications';
import { RestApiService } from './rest-api/rest-api.service';
import { Capacitor, PermissionState } from '@capacitor/core';
import { LoggerService } from './logger/logger.service';
import { UserNotificationService } from './user-notification/user-notification.service';
import { UserService } from './user/user.service';
import { ApiDevicePlatforms, ApiPushNotification, CapacitorPlatforms, PushNotificationEventTypes } from './rest-api/rest-api';
import { Router } from '@angular/router';
import { EMPTY, from } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

// check @capacitor/push-notifications
enum PushPermissionStatus {
  PROMPT = 'prompt',
  PROMPT_WITH_RATIONALE = 'prompt-with-rationale',
  GRANTED = 'granted',
  DENIED = 'denied'
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {
  private token: string | null = null;
  private alreadyCheckedPermission?: PermissionState;

  public constructor(
    private restApiService: RestApiService,
    private loggerService: LoggerService,
    private userNotificationService: UserNotificationService,
    private userService: UserService,
    private router: Router,
  ) {}

  public initNotifications(): void {
    if(
      !this.userService.loggedUser ||
      this.alreadyCheckedPermission === PushPermissionStatus.DENIED
    ) {
      return;
    }

    from(PushNotifications.requestPermissions())
    .pipe(
      mergeMap((result) => {
        this.alreadyCheckedPermission = result.receive;
        return result.receive === PushPermissionStatus.GRANTED
          ? from(PushNotifications.register()) // Register for push
          : EMPTY
      }),
      tap(() => {
        PushNotifications.addListener('registration', (token: Token) => {
          let platform = null;
          if(Capacitor.getPlatform() === CapacitorPlatforms.ANDROID) {
            platform = ApiDevicePlatforms.ANDROID;
          } else if (Capacitor.getPlatform() === CapacitorPlatforms.IOS) {
            platform = ApiDevicePlatforms.IOS;
          }

          if(platform && this.token !== token.value) {
            this.token = token.value;
            this.restApiService.sendDeviceToken({ deviceToken: token.value, platform }).subscribe({
              error: error => this.loggerService.error(error),
              next: () => this.tokenRegistered(),
            });
          } else {
            this.loggerService.logInfo('Push notifications token already registered, not sending it again.');
          }
        });

        PushNotifications.addListener('registrationError', (error: unknown) => {
          this.loggerService.error('Error on Push Notifications registration: ' + JSON.stringify(error));
        });
      })
    )
    .subscribe();
  }

  private tokenRegistered(): void {
    // prevent duplicit registration calls on Nth init
    PushNotifications.removeAllListeners().then(() => {
      PushNotifications.addListener('pushNotificationActionPerformed', (pushAction: ActionPerformed) => {
        const receivedNotification = pushAction.notification.data as ApiPushNotification;

        if(receivedNotification.notificationWebId) {
          this.restApiService.resolveNotification(receivedNotification.notificationWebId).subscribe({
            error: (error) =>
              this.loggerService.error('Failed to resolve notification ID:' + receivedNotification.notificationWebId, error),
            next: () => {
              this.userNotificationService.updateNotificationsCount();
            }
          });
        }
        if(receivedNotification.type) {
          this.redirectUser(receivedNotification);
        }
      });
    });
  }

  private redirectUser(receivedNotification: ApiPushNotification): void {
    if (
      receivedNotification.type === PushNotificationEventTypes.ADDED_DEPOSIT ||
      receivedNotification.type === PushNotificationEventTypes.ADDED_DEPOSIT_LESS_THEN_CASH_THRESHOLD ||
      receivedNotification.type === PushNotificationEventTypes.TRADING_COMPLETED ||
      receivedNotification.type === PushNotificationEventTypes.WITHDRAWAL_EXECUTION ||
      receivedNotification.type === PushNotificationEventTypes.WITHDRAWAL_REQUEST_ALL ||
      receivedNotification.type === PushNotificationEventTypes.WITHDRAWAL_REQUEST_PART
    ) {
      this.router.navigate(['/money']);
    } else if (receivedNotification.type === PushNotificationEventTypes.BLOG_CONFIRMED) {
      if (receivedNotification.blogId) {
        this.router.navigate(['/blog/' + receivedNotification.blogId]);
      } else {
        this.router.navigate(['/blog']);
      }
    } else if (receivedNotification.type === PushNotificationEventTypes.PORTFOLIO_LIQUIDATED) {
      this.router.navigate(['/notifications']);
    } else if (receivedNotification.type === PushNotificationEventTypes.PORTFOLIO_DECLINE) {
      this.router.navigate(['/investment-detail/' + (receivedNotification.portfolioId ?? NaN)]);
    }
  }

  public deletePushNotificationsToken(): void {
    let platform = null;
    if(Capacitor.getPlatform() === CapacitorPlatforms.ANDROID) {
      platform = ApiDevicePlatforms.ANDROID;
    } else if (Capacitor.getPlatform() === CapacitorPlatforms.IOS) {
      platform = ApiDevicePlatforms.IOS;
    }

    if(platform && this.token) {
      this.restApiService.deleteDeviceToken({ deviceToken: this.token, platform }).subscribe({
        error: error => this.loggerService.error(error),
        next: () => this.token = null,
      });
      PushNotifications.unregister();
      PushNotifications.removeAllListeners();
    }
  }
}
